/**
 * Standard breakpoints for responsive design based on those defined in
 * the Fluent UI, Coherent UI design systems and used by FH design team.
 *
 * Set up to be desktop first following design flows and use max-widths for media queries.
 */

import type { GriffelStyle } from '@fluentui/react-components';

type BreakpointFunction = (styles: GriffelStyle) => Record<string, GriffelStyle>;

interface IBreakpoints {
  /**
   * X-Small devices (portrait phones, less than 479px).
   */
  xs: BreakpointFunction;

  /**
   * Small devices (landscape phones, 480-639px).
   */
  s: BreakpointFunction;

  /**
   * Medium devices (tablets, 640-1023px).
   */
  m: BreakpointFunction;

  /**
   * Large devices (desktops, 1024-1365px).
   */
  l: BreakpointFunction;

  /**
   * X-Large devices (large desktops, 1366-1919px).
   */
  xl: BreakpointFunction;

  /**
   * XX-Large devices (xxlarge desktops, larger than 1920px).
   */
  xxl: BreakpointFunction;
}

// Standard 6 screen sizes for responsive design
export const screenSizes: Record<string, string> = {
  xs: '479px',
  s: '639px',
  m: '1023px',
  l: '1365px',
  xl: '1919px',
  xxl: '1920px',
};

// Breakpoints for responsive design using standard screen sizes
export const breakpoints: IBreakpoints = {
  xs: (style) => {
    return { [`@media (max-width: ${screenSizes['xs']})`]: style };
  },
  s: (style) => {
    return { [`@media (max-width: ${screenSizes['s']})`]: style };
  },
  m: (style) => {
    return { [`@media (max-width: ${screenSizes['m']})`]: style };
  },
  l: (style) => {
    return { [`@media (max-width: ${screenSizes['l']})`]: style };
  },
  xl: (style) => {
    return { [`@media (max-width: ${screenSizes['xl']})`]: style };
  },
  xxl: (style) => {
    return { '@media (min-width: 1920px)': style };
  },
};
